import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiHome, FiUsers, FiMessageSquare, FiAlertCircle, 
  FiSettings, FiLogOut, FiChevronLeft, FiChevronRight,
  FiPieChart, FiBarChart2, FiCreditCard, FiShield, FiGlobe
} from 'react-icons/fi';

interface NavbarProps {
  expanded?: boolean;
  mobile?: boolean;
  onToggleExpand?: () => void;
}

const AdminNavbar: React.FC<NavbarProps> = ({ 
  expanded = true, 
  mobile = false,
  onToggleExpand 
}) => {
  const location = useLocation();
  const [notificationCount, setNotificationCount] = useState(3);
  
  const isActive = (path: string) => {
    return location.pathname === path || (path !== '/admin' && location.pathname.startsWith(path));
  };
  
  const navItems = [
    { 
      name: 'Dashboard', 
      path: '/admin', 
      icon: <FiHome size={20} /> 
    },
    { 
      name: 'Users', 
      path: '/admin/users', 
      icon: <FiUsers size={20} /> 
    },
    { 
      name: 'Support Chats', 
      path: '/admin/chats', 
      icon: <FiMessageSquare size={20} />,
      highlight: true
    },
    { 
      name: 'Expert Requests', 
      path: '/admin/expert-requests', 
      icon: <FiAlertCircle size={20} />,
      highlight: true
    },
    { 
      name: 'Analytics', 
      path: '/admin/analytics', 
      icon: <FiBarChart2 size={20} /> 
    },
    { 
      name: 'Transactions', 
      path: '/admin/transactions', 
      icon: <FiCreditCard size={20} /> 
    },
  ];
  
  const secondaryNavItems = [
    { 
      name: 'Settings', 
      path: '/admin/settings', 
      icon: <FiSettings size={20} /> 
    },
    { 
      name: 'Logout', 
      path: '/login', 
      icon: <FiLogOut size={20} /> 
    }
  ];

  // Animation variants
  const navItemVariants = {
    initial: { opacity: 0, x: -20 },
    animate: (index: number) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: 0.05 * index,
        duration: 0.3,
        ease: "easeOut"
      }
    }),
    hover: {
      scale: 1.02,
      transition: { duration: 0.2 }
    }
  };

  const sidebarVariants = {
    expanded: { width: '16rem' },
    collapsed: { width: '5rem' }
  };
  
  if (mobile) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="h-8 w-8 rounded-full bg-gradient-to-r from-primary to-primary/60 flex items-center justify-center text-white font-bold mr-2">
              M
            </div>
            <h1 className="text-lg font-semibold">Matrix Admin</h1>
          </div>
        </div>
        
        <nav className="mt-6 flex-grow">
          <ul className="space-y-2">
            {navItems.map((item, index) => (
              <motion.li 
                key={item.path}
                custom={index}
                initial="initial"
                animate="animate"
                whileHover="hover"
                variants={navItemVariants}
              >
                <Link
                  to={item.path}
                  className={`flex items-center px-4 py-3 rounded-lg text-base transition-all ${
                    isActive(item.path)
                      ? 'bg-primary/10 text-primary font-medium border border-primary/20'
                      : 'hover:bg-card hover:shadow-sm border border-transparent'
                  }`}
                >
                  <span className="mr-3">{item.icon}</span>
                  <span>{item.name}</span>
                  {item.highlight && notificationCount > 0 && (
                    <span className="ml-auto bg-primary text-white text-xs font-medium px-2 py-0.5 rounded-full">
                      {notificationCount}
                    </span>
                  )}
                </Link>
              </motion.li>
            ))}
          </ul>
        </nav>
        
        <div className="mt-auto pt-6 border-t border-border">
          <ul className="space-y-2">
            {secondaryNavItems.map((item, index) => (
              <motion.li 
                key={item.path}
                custom={index}
                initial="initial"
                animate="animate"
                whileHover="hover"
                variants={navItemVariants}
              >
                <Link
                  to={item.path}
                  className={`flex items-center px-4 py-3 rounded-lg text-base transition-all ${
                    isActive(item.path)
                      ? 'bg-primary/10 text-primary font-medium border border-primary/20'
                      : 'hover:bg-card hover:shadow-sm border border-transparent'
                  }`}
                >
                  <span className="mr-3">{item.icon}</span>
                  <span>{item.name}</span>
                </Link>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    );
  }
  
  return (
    <motion.aside
      initial={expanded ? "expanded" : "collapsed"}
      animate={expanded ? "expanded" : "collapsed"}
      variants={sidebarVariants}
      transition={{ type: "spring", stiffness: 400, damping: 35 }}
      className="h-full bg-foreground/80 backdrop-blur-md border-r border-border shadow-sm overflow-hidden"
    >
      <div className="h-full flex flex-col p-4 relative">
        <div className={`flex items-center ${expanded ? 'justify-between' : 'justify-center'} mb-6`}>
          {expanded ? (
            <>
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-gradient-to-r from-primary to-primary/60 flex items-center justify-center text-white font-bold mr-2">
                  M
                </div>
                <h1 className="text-lg font-semibold">Matrix Admin</h1>
              </div>
              <button 
                onClick={onToggleExpand}
                className="p-2 rounded-full hover:bg-card/70 focus:outline-none group relative"
                aria-label="Collapse sidebar"
              >
                <FiChevronLeft size={18} />
                <span className="absolute inset-0 rounded-full border border-primary/0 group-hover:border-primary/20 transition-colors"></span>
              </button>
            </>
          ) : (
            <>
              <div className="h-10 w-10 rounded-full bg-gradient-to-r from-primary to-primary/60 flex items-center justify-center text-white font-bold">
                M
              </div>
              <button 
                onClick={onToggleExpand}
                className="absolute -right-3 top-8 p-1.5 rounded-full bg-foreground/90 backdrop-blur-md border border-border shadow-sm hover:bg-card group relative"
                aria-label="Expand sidebar"
              >
                <FiChevronRight size={14} />
                <span className="absolute inset-0 rounded-full border border-primary/0 group-hover:border-primary/20 transition-colors"></span>
              </button>
            </>
          )}
        </div>
        
        <nav className="mt-6 flex-grow overflow-y-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
          <div className={`mb-2 px-4 py-2 text-text/60 text-xs uppercase tracking-wider ${!expanded && 'sr-only'}`}>
            Main Menu
          </div>
          <ul className="space-y-1">
            {navItems.map((item, index) => (
              <motion.li 
                key={item.path}
                custom={index}
                initial="initial"
                animate="animate"
                whileHover="hover"
                variants={navItemVariants}
              >
                <Link
                  to={item.path}
                  className={`flex items-center px-4 py-3 rounded-lg transition-all ${
                    isActive(item.path)
                      ? 'bg-primary/10 text-primary font-medium border border-primary/20 shadow-glow-sm'
                      : 'hover:bg-card/70 border border-transparent hover:border-border/70'
                  }`}
                >
                  <span className={`flex-shrink-0 ${expanded ? 'mr-3' : 'mx-auto'}`}>{item.icon}</span>
                  {expanded && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.2, delay: 0.1 }}
                    >
                      {item.name}
                    </motion.span>
                  )}
                  {item.highlight && notificationCount > 0 && (
                    <>
                      {expanded ? (
                        <span className="ml-auto bg-primary text-white text-xs font-medium px-2 py-0.5 rounded-full">
                          {notificationCount}
                        </span>
                      ) : (
                        <span className="absolute top-2 right-2 h-4 w-4 bg-primary text-white text-xs font-medium rounded-full flex items-center justify-center">
                          {notificationCount}
                        </span>
                      )}
                    </>
                  )}
                </Link>
              </motion.li>
            ))}
          </ul>
          
          <div className={`mt-8 mb-2 px-4 py-2 text-text/60 text-xs uppercase tracking-wider ${!expanded && 'sr-only'}`}>
            System
          </div>
          <ul className="space-y-1">
            {secondaryNavItems.map((item, index) => (
              <motion.li 
                key={item.path}
                custom={index + navItems.length}
                initial="initial"
                animate="animate"
                whileHover="hover"
                variants={navItemVariants}
              >
                <Link
                  to={item.path}
                  className={`flex items-center px-4 py-3 rounded-lg transition-all ${
                    isActive(item.path)
                      ? 'bg-primary/10 text-primary font-medium border border-primary/20 shadow-glow-sm'
                      : 'hover:bg-card/70 border border-transparent hover:border-border/70'
                  }`}
                >
                  <span className={`flex-shrink-0 ${expanded ? 'mr-3' : 'mx-auto'}`}>{item.icon}</span>
                  {expanded && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.2, delay: 0.1 }}
                    >
                      {item.name}
                    </motion.span>
                  )}
                </Link>
              </motion.li>
            ))}
          </ul>
        </nav>
        
        {expanded && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-auto pt-6 border-t border-border"
          >
            <div className="p-4 bg-card/30 rounded-lg border border-border">
              <div className="flex items-center mb-3">
                <div className="p-2 rounded-md bg-primary/10 mr-3">
                  <FiShield size={16} className="text-primary" />
                </div>
                <h3 className="text-sm font-medium">System Status</h3>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-text/70">API Status</span>
                <span className="flex items-center text-xs font-medium text-green-500">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-500 mr-1"></span>
                  Active
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs text-text/70">Response Time</span>
                <span className="text-xs font-medium">240ms</span>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.aside>
  );
};

export default AdminNavbar; 